import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Alert,
  Menu, MenuItem,
  Snackbar, TablePagination, Chip, Stack, useTheme, useMediaQuery
} from '@mui/material';
import { Plus, MoreVertical, Trash2, Edit, Users } from 'lucide-react';
import axios from '../utils/axios';
import CustomerEnquiryDataModal from '../components/CustomerEnquiryDataModal';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories, fetchCountries, fetchStates, fetchCities } from '../redux/slices/businessSlice';

const CustomerEnquiryDataView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { categories, countries, states, cities } = useSelector((state) => state.business);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Modal & Row actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionItem, setActionItem] = useState(null);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/customer-enquiry-data?page=${page + 1}&limit=${rowsPerPage}`);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to fetch customer enquiry data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchCountries());
    dispatch(fetchStates());
    dispatch(fetchCities());
    loadData();
  }, [dispatch, loadData]);

  const handleMenuOpen = (event, item) => {
    setMenuAnchorEl(event.currentTarget);
    setActionItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActionItem(null);
  };

  const handleEditClick = () => {
    setEditingItem(actionItem);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setMenuAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (actionItem) {
        await axios.delete(`/customer-enquiry-data/${actionItem._id}`, { skipNotify: 'success' });
        setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
        loadData();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.msg || 'Failed to delete', severity: 'error' });
    }
    setDeleteDialogOpen(false);
    setActionItem(null);
  };

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Customer Enquiry Data"
        subtitle="Manage raw customer inquiry records, contact numbers, and geographical classifications."
        count={total}
        countLabel="records"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Engagement', path: '/enquiries' },
          { label: 'Customer Enquiry Data' },
        ]}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ExportTableButton
              data={data}
              total={total}
              onExportAll={async () => {
                const res = await axios.get('/customer-enquiry-data', { params: { all: true } });
                return res.data.data || [];
              }}
              columns={[
                { label: 'Phone Number', key: 'number' },
                { label: 'Category', key: 'category' },
                { label: 'City', key: 'city' },
                { label: 'State', key: 'state' },
                { label: 'Country', key: 'country' },
              ]}
              filename="customer_enquiry_data"
            />
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
            >
              Add Record
            </Button>
          </Stack>
        }
      />

      <Card sx={{ overflow: 'hidden', position: 'relative' }}>
        {error ? (
          <Box sx={{ px: 3, pt: 3, pb: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableLoadingSkeleton rows={rowsPerPage} columns={4} />
                  ) : data.length > 0 ? data.map((item) => (
                    <TableRow key={item._id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{item.number}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.category}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {item.city}, {item.state}, {item.country}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, item)}>
                          <MoreVertical size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 0 }}>
                        <EmptyState
                          icon={Users}
                          title="No customer enquiry data found"
                          description="Add raw customer enquiry records to track and assign inbound inquiries."
                          actionLabel="Add Record"
                          onAction={() => { setEditingItem(null); setIsModalOpen(true); }}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={isMobile ? "" : "Rows per page:"}
                sx={{
                  border: 'none',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                    display: isMobile ? 'none' : 'block',
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { width: 140, boxShadow: '0 8px 16px 0 rgba(145, 158, 171, 0.24)', borderRadius: 2 } }}
      >
        <MenuItem onClick={handleEditClick} sx={{ fontSize: '0.875rem', gap: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {isModalOpen && (
        <CustomerEnquiryDataModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={editingItem}
          categories={categories}
          countries={countries}
          states={states}
          cities={cities}
          onSuccess={() => {
            loadData();
            setSnackbar({ open: true, message: `Record ${editingItem ? 'updated' : 'added'} successfully!`, severity: 'success' });
          }}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Record"
        content="Are you sure you want to delete this customer enquiry record? This action cannot be undone."
        confirmText="Delete"
        severity="error"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerEnquiryDataView;
